import React from 'react';
import { format } from 'date-fns';
import { commaSeparatedNumbers } from '../../utils/commaSeparatedNumbers';

interface BphData {
  time: string;
  activeJobs: string[];
  totalBphOfJobs: number;
  actual?: number | null | undefined;
  reason?: string | null | undefined;
}

interface CleanSection {
  label: string;
  value: string | number;
}

interface TransformedCleanData {
  cleanId: string;
  totalChangeoverR2: number;
  sections: CleanSection[];
}

interface PrintPreviewComponentProps {
  selectedRoomName: string;
  selectedShift: string;
  bphData: BphData[];
  transformedCleanData: TransformedCleanData[];
  totalBph: number;
  numberOfBottlesInAtShiftStart?: number | null;
}

export const PrintPreviewComponent: React.FC<PrintPreviewComponentProps> = ({
  selectedRoomName,
  selectedShift,
  bphData,
  transformedCleanData,
  totalBph,
  numberOfBottlesInAtShiftStart
}) => {
  // Label map for consistent display
  const labelMap: { [key: string]: string } = {
    'Clean': 'Clean (CL)',
    'Clear': 'Clear (CR)',
    'Setup': 'Setup (ST)',
    'LV': 'Verify (LV)',
    'Verify': 'Verify (LV)',
    'Clean (CL)': 'Clean (CL)',
    'Clear (CR)': 'Clear (CR)',
    'Setup (ST)': 'Setup (ST)',
    'Verify (LV)': 'Verify (LV)'
  };

  const borderColor = '#d6b1fd';
  const headerBg = '#4d2e6d';
  const subHeaderBg = '#f6edff';

  // Format date as MM/DD/YY (month/day/year)
  const formatDate = (dateTimeString: string) => {
    const [datePart] = dateTimeString.split(' ');
    // datePart is already in format: "10-21-25" (MM-DD-YY)
    const parts = datePart.split('-');
    const month = parts[0];  // "10"
    const day = parts[1];    // "21"
    const year = parts[2];   // "25"
    // Return MM/DD/YY format: "10/21/25"
    return `${month}/${day}/${year}`;
  };

  return (
    <div style={{ width: '100%', height: '100%', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '13px', padding: '4px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Print Header */}
      <div style={{ marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '20px' }}>
        <div style={{ flex: 1, display: 'flex', gap: '20px', fontSize: '16px', color: '#333', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold' }}>Room: {selectedRoomName}</span>
          <span style={{ fontWeight: 'bold' }}>Shift: {selectedShift}</span>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>Visibility Board</span>
        </div>
        <div style={{ flex: 1, textAlign: 'right', fontSize: '11px', color: '#555' }}>
          <span style={{ fontWeight: '500' }}>Print Date: {format(new Date(), 'MMMM dd, yyyy h:mm a')}</span>
        </div>
      </div>

      {/* Main Container */}
      <div style={{ display: 'flex', gap: '6px', width: '100%', flex: 1, overflow: 'hidden' }}>
        {/* Left Section - Bottles Per Hour */}
        <div style={{ width: '44%', display: 'flex', flexDirection: 'column' }}>
          {/* Main Header */}
          <div style={{ backgroundColor: headerBg, color: 'white', fontSize: '14px', fontWeight: '600', textAlign: 'center', padding: '6px', border: `1px solid ${borderColor}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            BOTTLES PER HOUR
          </div>

          {/* Combined Header Rows */}
          <div style={{ display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none' }}>
            {/* Date/Time column spanning 2 rows */}
            <div style={{ width: '12.54%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
              <div style={{ backgroundColor: subHeaderBg, borderRight: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}`, padding: '4px 1px', fontSize: '9px', fontWeight: '600', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, boxSizing: 'border-box', lineHeight: '1.1' }}>
                Date/Time
              </div>
            </div>
            
            {/* Right side with two rows */}
            <div style={{ width: '87.46%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
              {/* First row: In-process header */}
              <div style={{ backgroundColor: subHeaderBg, borderBottom: `1px solid ${borderColor}`, padding: '6px 2px', fontSize: '12px', fontWeight: '600', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxSizing: 'border-box' }}>
                <span> # of bottles for In-process Job:</span>
                <span style={{ fontWeight: '700', color: headerBg }}>
                  {numberOfBottlesInAtShiftStart !== undefined && numberOfBottlesInAtShiftStart !== null
                    ? commaSeparatedNumbers(numberOfBottlesInAtShiftStart)
                    : '-'}
                </span>
              </div>
              
              {/* Second row: Column headers */}
              <div style={{ display: 'flex', backgroundColor: subHeaderBg, fontSize: '11px', fontWeight: '600' }}>
                <div style={{ width: '20.05%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', textAlign: 'center', boxSizing: 'border-box' }}>Job ID</div>
                <div style={{ width: '39.1%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', textAlign: 'center', boxSizing: 'border-box' }}>R2</div>
                <div style={{ width: '21.95%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', textAlign: 'center', boxSizing: 'border-box' }}>Actual</div>
                <div style={{ width: '18.89%', padding: '6px 2px', textAlign: 'center', boxSizing: 'border-box' }}>Comments</div>
              </div>
            </div>
          </div>

          {/* Data Rows */}
          {bphData.map((item, index) => (
            <div key={index} style={{ display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none', minHeight: '32px', fontSize: '11px', color: '#5a5959' }}>
              <div style={{ width: '12.54%', borderRight: `1px solid ${borderColor}`, padding: '2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1.2', fontSize: '10px', boxSizing: 'border-box' }}>
                {formatDate(item.time)}
                <br />
                {item.time.split(' ').slice(1).join(' ')}
              </div>
              <div style={{ width: '17.54%', borderRight: `1px solid ${borderColor}`, padding: '2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', wordBreak: 'break-word', boxSizing: 'border-box' }}>
                {item.activeJobs[0] || ''}
              </div>
              <div style={{ width: '15%', borderRight: `1px solid ${borderColor}`, padding: '2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', boxSizing: 'border-box' }}>
                {item.totalBphOfJobs ? commaSeparatedNumbers(item.totalBphOfJobs) : ''}
              </div>
              <div style={{ width: '19.2%', borderRight: `1px solid ${borderColor}`, padding: '2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', boxSizing: 'border-box' }}>
                {/* This R2 column is always blank */}
              </div>
              <div style={{ width: '19.2%', borderRight: `1px solid ${borderColor}`, padding: '2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                {index > 0 ? (item.actual || '') : ''}
              </div>
              <div style={{ width: '16.52%', padding: '2px 4px', textAlign: 'left', display: 'flex', alignItems: 'center', wordBreak: 'break-word', boxSizing: 'border-box' }}>
                {index > 0 ? (item.reason || '') : ''}
              </div>
            </div>
          ))}

          {/* Shift Total Row */}
          <div style={{ display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none', minHeight: '32px', fontWeight: '600', fontSize: '11px', color: '#5a5959' }}>
            <div style={{ width: '12.54%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', boxSizing: 'border-box' }}></div>
            <div style={{ width: '17.54%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>SHIFT TOTAL</div>
            <div style={{ width: '15%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
              {totalBph ? commaSeparatedNumbers(totalBph) : ''}
            </div>
            <div style={{ width: '19.2%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', boxSizing: 'border-box' }}></div>
            <div style={{ width: '19.2%', borderRight: `1px solid ${borderColor}`, padding: '6px 2px', boxSizing: 'border-box' }}></div>
            <div style={{ width: '16.52%', padding: '6px 2px', boxSizing: 'border-box' }}></div>
          </div>

          {/* Additional Comments Section */}
          <div style={{ width: '100%', border: `1px solid ${borderColor}`, marginTop: '6px' }}>
            <div style={{ backgroundColor: headerBg, color: 'white', fontSize: '13px', fontWeight: '600', padding: '4px', textAlign: 'left' }}>
              Additional Comments
            </div>
            <div style={{ width: '100%' }}>
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    height: '28px',
                    width: '100%',
                    borderBottom: index < 5 ? '1px dashed #999' : 'none',
                    boxSizing: 'border-box'
                  }}
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Section - Changeovers */}
        <div style={{ width: '56%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Main Header */}
          <div style={{ backgroundColor: headerBg, color: 'white', fontSize: '14px', fontWeight: '600', textAlign: 'center', padding: '6px', border: `1px solid ${borderColor}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            CHANGE OVERS
          </div>

          {/* Render changeover data - always exactly 4 sections */}
          {Array.from({ length: 4 }).map((_, index) => {
            const cleanItem = index < transformedCleanData.length 
              ? transformedCleanData[index]
              : {
                  cleanId: '',
                  totalChangeoverR2: 0,
                  sections: [
                    { label: 'Clean (CL)', value: '' },
                    { label: 'Clear (CR)', value: '' },
                    { label: 'Setup (ST)', value: '' },
                    { label: 'Verify (LV)', value: '' }
                  ]
                };
            
            return (
            <div key={index}>
              {/* Summary Row - aligned with columns below */}
              <div style={{ display: 'flex', backgroundColor: subHeaderBg, border: `1px solid ${borderColor}`, borderTop: 'none', fontSize: '11.5px', fontWeight: '600', minHeight: '22px' }}>
                <div style={{ width: '18%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  Job and Level
                </div>
                <div style={{ width: '10%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  Total R2:
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: '#f3f3f3', color: '#d8000c', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  {cleanItem.totalChangeoverR2 ? commaSeparatedNumbers(cleanItem.totalChangeoverR2) : ''}
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  Total Actual:
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', backgroundColor: '#f3f3f3', boxSizing: 'border-box' }}></div>
                <div style={{ width: '8%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  Total Var:
                </div>
                <div style={{ width: '28%', padding: '3px 1px', backgroundColor: '#f3f3f3', boxSizing: 'border-box' }}></div>
              </div>

              {/* Job ID Header Row */}
              <div style={{ display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none', fontSize: '10.35px', minHeight: '22px' }}>
                <div style={{ width: '18%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center', wordBreak: 'break-word', fontSize: '10.35px', boxSizing: 'border-box' }}>
                  {cleanItem.cleanId}
                </div>
                <div style={{ width: '10%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12.42px', boxSizing: 'border-box' }}>
                  R2
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', lineHeight: '1.1', boxSizing: 'border-box' }}>
                  Start Time
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.8px', lineHeight: '1.1', boxSizing: 'border-box' }}>
                  Expected Stop Time
                </div>
                <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.8px', lineHeight: '1.1', boxSizing: 'border-box' }}>
                  Actual Stop Time
                </div>
                <div style={{ width: '8%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.35px', boxSizing: 'border-box' }}>
                  Variance
                </div>
                <div style={{ width: '28%', padding: '3px 1px', textAlign: 'center', backgroundColor: headerBg, color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12.42px', boxSizing: 'border-box' }}>
                  Comments
                </div>
              </div>

              {/* Section Rows */}
              {cleanItem.sections.map((section, sectionIndex) => {
                const displayLabel = labelMap[section.label] || section.label;
                return (
                  <div key={sectionIndex} style={{ display: 'flex', border: `1px solid ${borderColor}`, borderTop: 'none', fontSize: '11.5px', minHeight: '24px', color: '#5a5959' }}>
                    <div style={{ width: '18%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.35px', boxSizing: 'border-box' }}>
                      {displayLabel}
                    </div>
                    <div style={{ width: '10%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                      {section.value || ''}
                    </div>
                    <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', boxSizing: 'border-box' }}></div>
                    <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', boxSizing: 'border-box' }}></div>
                    <div style={{ width: '16%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', boxSizing: 'border-box' }}></div>
                    <div style={{ width: '8%', borderRight: `1px solid ${borderColor}`, padding: '3px 1px', boxSizing: 'border-box' }}></div>
                    <div style={{ width: '28%', padding: '3px 1px', boxSizing: 'border-box' }}></div>
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
